<template>
    <div class="kulturplatz-entry-container">
        <v-container fluid class="form-content overflow-auto pa-5">
            <div class="d-flex align-center">
                <h4 class="mb-2">{{ $t('kulturplatz') }}</h4>
                <v-spacer />

                <PublishedIndicator :record="record" />
            </div>

            <div v-if="userIsOrganizer()">
                <p class="tgk-paragraph-text mt-2">{{ $t('kulturplatz_infotext') }}</p>
                <div class="d-flex align-center">
                    <v-checkbox
                        :input-value="record.is_visible"
                        dense
                        hide-details
                        :label="$t('publication_request_text')"
                        @change="onInput('is_visible', $event)"
                    />
                    &nbsp;
                    <TooltipIcon :text="$t('kulturplatz_desc_tip')" />
                </div>
            </div>

            <!-- Bild -->
            <p class="tgk-paragraph-text mt-5">{{ $t('img_kulturplatz') }}</p>
            <div v-if="!featuredImageThumbUrl()">
                <div class="d-flex" style="gap: 10px">
                    <div>
                        <ImageUploadCard width="240px" @uploaded="afterImageUploaded" />
                        <ActionBtn width="240px" class="mt-5" @click="imgSelectDlg = true">
                            <KpIcon icon-cls="mediamanager" color="white" />
                            <span>{{ $t('media_manager') }}</span>
                        </ActionBtn>
                    </div>
                    <div class="flex-grow-1">
                        <TooltipIcon :html="$t('event_cover_image_requirements')" icon-cls="info" icon-size="24" />
                    </div>
                </div>
            </div>
            <div v-else>
                <div>
                    <v-img :src="`${featuredImageThumbUrl()}?ts=${cacheBuster}`" width="220px" height="156px"> </v-img>
                </div>
                <div class="d-flex">
                    <PlainBtn @click="imgSelectDlg = true">
                        <KpIcon icon-cls="pencil" color="grey" />
                        <span>{{ $t('edit') }}</span>
                    </PlainBtn>
                    <PlainBtn @click="deleteFeaturedImage">
                        <KpIcon icon-cls="trash" color="grey" />
                        <span>{{ $t('delete') }}</span>
                    </PlainBtn>
                </div>
            </div>

            <!-- Beschreibung -->
            <LabelField :label="$t('description')" class="mt-5">
                <TinymceEditor
                    :value="record.description"
                    :init="tinymceConfig()"
                    @input="record.set('description', $event)"
                />
            </LabelField>

            <!-- Kategorisierung -->
            <v-row dense>
                <v-col>
                    <h4 class="mt-5">{{ $t('categorize') }}</h4>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('kat_fuer_kultplatz')">
                        <CategoryMultiselect
                            :value="record.category_ids"
                            :for-address="record.type === 'address'"
                            :for-location="record.type === 'location'"
                            :for-event-organizer="record.type === 'event_organizer'"
                            :placeholder="$t('choose_multi')"
                            @change="record.set('category_ids', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField
                        :label="$t('interessen_tags_erweiterte_suche')"
                        :label-tip="$t('interessen_tags_erweiterte_suche_tip')"
                    >
                        <TagSelect
                            for-address
                            :value="record.tags"
                            :show-download="false"
                            :show-add="false"
                            item-value="id"
                            :placeholder="$t('choose_multi')"
                            hide-details="auto"
                            @input="record.set('tags', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>

            <!-- Social Media -->
            <v-row dense>
                <v-col>
                    <h4 class="mt-2">{{ $t('socialmedia_links') }}</h4>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <MultiItemContainer
                        :value="record.social_media"
                        :show-text-field="false"
                        text-name="description"
                        item-name="url"
                        type-name="type"
                        container-class="d-flex"
                        item-class="flex-grow-1"
                        action-class="flex-row"
                        @input="record.set('social_media', $event)"
                        @change="record.set('social_media', $event)"
                    >
                        <template #type="slotProps">
                            <SocialMediaSelect :value="slotProps.value" class="ml-1" @change="slotProps.input" />
                        </template>
                        <template #default="slotProps">
                            <UrlField :value="slotProps.value" @input="slotProps.input" />
                        </template>
                    </MultiItemContainer>
                </v-col>
            </v-row>

            <div class="d-flex justify-end mt-5">
                <ActionBtn :disabled="!value" @click="$emit('save-request')">
                    <KpIcon icon-cls="calendar" color="white" disabled-color="grey" left />
                    {{ $t('save_addr') }}
                </ActionBtn>
            </div>
        </v-container>

        <AssignedAddressesPanel
            ref="assignedAddressesPanel"
            class="kontakt-content pa-5"
            :record="record"
            :specific-record="specificRecord"
            @saving="$emit('saving', $event)"
            @assign-event-organizer="$emit('assign-event-organizer', $event)"
        />

        <MMSelectDialog
            v-if="imgSelectDlg"
            v-model="imgSelectDlg"
            :image-formats-config="imageFormatConfig"
            :image-crop-data="
                specificRecord ? specificRecord.featured_img_crop_config : record.featured_img_crop_config
            "
            :initial-image="imageRec"
            :initial-step="imageRec ? 2 : 1"
            @image-select="onImageSelect"
            @image-crop="onImageCrop"
        />
    </div>
</template>

<script>
import { formRules } from '@/helpers/tools';
import { Address } from '@/models/Address';
import GlobalsMixin from '@/mixins/globals';
import { userIsOrganizer } from '@/helpers/auth';
import PublishedIndicator from '@/components/address/PublishedIndicator.vue';
import ImageUploadCard from '@/components/ImageUploadCard.vue';
import { Location } from '@/models/Location';
import { EventOrganizer } from '@/models/EventOrganizer';
import MMSelectDialog from '@/components/media_manager/SelectFormatDialog.vue';
import CategoryMultiselect from '@/components/category/multiselect.vue';
import TagSelect from '@/components/tag/select.vue';
import { mapActions } from 'vuex';
import { errToStr } from '@/helpers/tools';
import SocialMediaSelect from '@/components/SocialMediaSelect.vue';
import MultiItemContainer from '@/components/multi_item/Container.vue';
import AssignedAddressesPanel from '@/components/address/AssignedAddressesPanel.vue';
import { Image } from '@/models/Image';

export default {
    name: 'AddressEditKulturplatz',
    components: {
        PublishedIndicator,
        ImageUploadCard,
        MMSelectDialog,
        CategoryMultiselect,
        TagSelect,
        SocialMediaSelect,
        MultiItemContainer,
        AssignedAddressesPanel,
    },
    mixins: [GlobalsMixin],
    props: {
        record: {
            type: Address,
            required: true,
        },
        specificRecord: {
            type: [Location, EventOrganizer],
            default: null,
        },
        // used to indicate form validity
        value: Boolean,
    },
    data() {
        return {
            imgSelectDlg: false,
            cacheBuster: new Date().getTime(),
        };
    },
    computed: {
        imageFormatConfig() {
            if (this.specificRecord instanceof EventOrganizer) {
                return this.imageConfig.organizer;
            }
            if (this.specificRecord instanceof Location) {
                return this.imageConfig.location;
            }
            return this.imageConfig.address;
        },

        imageRec() {
            // depending on the presence of the specific record, we deliver the one or the
            // other image (if already present in the store)
            return this.specificRecord
                ? this.$store.getters['image/imageById'](this.specificRecord.featured_image_id)
                : this.$store.getters['image/imageById'](this.record.featured_image_id);
        },
    },
    watch: {
        record(act) {
            if (act && act.featured_image_id) {
                this.loadImage({ imageId: act.featured_image_id });
            }
        },
        specificRecord(act) {
            if (act && act.featured_image_id) {
                this.loadImage({ imageId: act.featured_image_id });
            }
        },
    },

    created() {
        // static view variables:
        this.imageConfig = this.globalVar('featured_images_config') || {};
        this.rules = formRules;
    },

    mounted() {
        this.loadImage({ imageId: this.record.featured_image_id });
    },
    methods: {
        ...mapActions('image', ['loadImage']),
        userIsOrganizer,

        // can / will be called from the parent component to initiate a forceful form validation
        validateForm() {
            // this.$refs.form.validate();
        },
        onInput(field, value) {
            this.record.set(field, value);
        },

        /**
         * When the image is uploaded, we need to create default
         * crop versions, and store it.
         */
        async afterImageUploaded(images, flowFiles) {
            if (images && images.length) {
                const img = new Image(images[0]);
                await this.onImageCrop(null, img);
            }
        },

        /** Image selected from media manager, no crop: */
        async onImageSelect(image) {
            if (image) {
                await this.onImageCrop(null, image);
            }
        },

        async onImageCrop(cropData, image) {
            if (image) {
                this.imgSelectDlg = false;
                const recForImage = this.specificRecord ? this.specificRecord : this.record;
                await this.createFeaturedImage(cropData, image, recForImage);
            }
        },

        featuredImageThumbUrl() {
            if (this.specificRecord) {
                return this.specificRecord.thumbImageUrl;
            }
            if (this.record) {
                return this.record.thumbImageUrl;
            }
            return null;
        },

        deleteFeaturedImage() {
            if (this.specificRecord) {
                this.specificRecord.set('featured_image_id', null);
            }
            this.record.set('featured_image_id', null);
            this.$emit('save-request');
        },

        async createFeaturedImage(cropData, image, record) {
            if (image) {
                try {
                    this.$emit('saving', true);
                    let entity = 'address';
                    if (record instanceof Location) {
                        entity = 'location';
                    } else if (record instanceof EventOrganizer) {
                        entity = 'organizer';
                    }
                    await image.createFeaturedImage(entity, cropData, record.id);
                    record.set({
                        featured_image_id: image.id,
                        featured_img_crop_config: cropData || null,
                    });
                    this.cacheBuster = new Date().getTime();
                    this.loadImage({ imageId: image.id });
                    // we need to save the record / specific record, as we now have set a
                    // featured image id:
                    this.$emit('save-request');
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                }
            }
        },

        tinymceConfig() {
            return {
                toolbar1:
                    'link unlink | image | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | code',
                toolbar2: '',
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.kulturplatz-entry-container {
    display: grid;
    grid-template-columns: minmax(450px, 1fr) minmax(400px, auto);
    grid-template-areas: 'form kontakte';
}

.form-content {
    grid-area: form;
    overflow: auto;
}

.kontakt-content {
    grid-area: kontakte;
    background-color: var(--toolbar-bg-color);
    overflow: auto;
}
</style>
